import { formatAbn } from "utils/abn";
import { rctiTimestamp } from "utils/rctiTimestamp";
import { downloadRctiAttachment } from "utils/rctiAttachmentAPI";
import { rctiLineTax } from "utils/rctiLineTax";
import React from "react";
import PropTypes from "prop-types";
import { agreementReference } from "./RctiDesignSystem";
import { LuDownload, LuFileText, LuLockKeyhole } from "react-icons/lu";

export default function RCTIDocument({
  values,
  items,
  documents,
  organisation,
  rctiID,
  loadingPage,
  currencyCode,
  subtotalExcludingGst,
  gstSummaryLabel,
  total,
  money,
  dateValue,
  showAttachments = true,
  issuedDetail = false,
  agreement = null,
}) {
  const issued = Boolean(values.issuedAt || Number(values.emailSent) > 0);
  const buyer = values.buyerIdentity || {};
  const supplier = values.supplierIdentity || {};
  const buyerRepresentative =
    buyer.representativeName || agreement?.buyerRepresentative?.name;
  const buyerRole =
    buyer.representativeRole || agreement?.buyerRepresentative?.position;
  const supplierRepresentative =
    supplier.representativeName ||
    agreement?.supplierSigner?.name ||
    agreement?.supplierRepresentative?.name;
  const supplierRole =
    supplier.representativeRole ||
    agreement?.supplierSigner?.position ||
    agreement?.supplierRepresentative?.position;
  const legalNotes =
    values.rctiNotes ||
    values.supplierNotes ||
    values.scope ||
    agreement?.scope ||
    "";
  return (
    <section
      className="invoice-flow-card invoice-content-card rcti-detail-document"
      data-pdf-document-id={rctiID}
      data-pdf-ready={!loadingPage && Boolean(values.rctiNumber)}
    >
      <div className="rcti-paper-heading">
        <div className="rcti-paper-business">
          <span className="rcti-type-emphasis">{organisation}</span>
        </div>
        <span className="rcti-status status-draft">
          {values.lifecycleStatus === "invalidated"
            ? "Archived · Invalidated"
            : values.issuedAt || Number(values.emailSent) > 0
            ? "Issued · Locked"
            : "Draft / preview"}
        </span>
      </div>
      <div className="rcti-paper-identity">
        <h2>Recipient Created Tax Invoice</h2>
        <span className="rcti-type-emphasis">
          {issued
            ? `${values.rctiNumber || "RCTI"} · Version ${values.version || 1}`
            : "Number assigned only on successful issue"}
        </span>
      </div>
      <hr />
      {values.lifecycleStatus === "invalidated" && (
        <p className="rcti-design-notice">
          Historical version replaced by a correction. This copy does not create
          another payable. {values.invalidationReason}
        </p>
      )}
      <div className="rcti-paper-parties">
        <div>
          <span>Buyer / recipient / issuer</span>
          <strong>{organisation}</strong>
          {(values.buyerAbn || buyer.abnNumber) && (
            <small>ABN {formatAbn(values.buyerAbn || buyer.abnNumber)}</small>
          )}
          {buyerRepresentative && (
            <small>
              {buyerRepresentative}
              {buyerRole ? ` · ${buyerRole}` : ""}
            </small>
          )}
          {buyer.emailAddress && <small>{buyer.emailAddress}</small>}
          {buyer.address && <small>{buyer.address}</small>}
        </div>
        <div>
          <span>Supplier / vendor</span>
          <strong>
            {supplier.name ||
              values.supplierCompanyName ||
              values.supplierName ||
              "—"}
          </strong>
          {(values.supplierAbn || supplier.abnNumber) && (
            <small>
              ABN {formatAbn(values.supplierAbn || supplier.abnNumber)}
            </small>
          )}
          {supplierRepresentative && (
            <small>
              {supplierRepresentative}
              {supplierRole ? ` · ${supplierRole}` : ""}
            </small>
          )}
          {(values.supplierEmail || supplier.emailAddress) && (
            <small>{values.supplierEmail || supplier.emailAddress}</small>
          )}
          {supplier.address && <small>{supplier.address}</small>}
        </div>
      </div>
      {issuedDetail ? (
        <div className="rcti-c13-document-meta">
          <div>
            <span>Issue date</span>
            <strong>{rctiTimestamp(values.issuedAt || values.rctiDate)}</strong>
          </div>
          {values.dueDate && (
            <div>
              <span>Payment due</span>
              <strong>{dateValue(values.dueDate)}</strong>
            </div>
          )}
          <div>
            <span>Supply / value determined</span>
            <strong>
              {values.supplyDate
                ? dateValue(values.supplyDate)
                : "Not recorded"}
            </strong>
          </div>
          <div>
            <span>Accepted agreement</span>
            <strong>
              {values.rctiAgreementID || agreement?.reference
                ? `${
                    agreement?.reference ||
                    agreementReference(values.rctiAgreementID)
                  } · Accepted wording v${agreement?.version || "1.0"}`
                : "Not recorded"}
            </strong>
          </div>
        </div>
      ) : (
        <>
          {" "}
          <div className="rcti-detail-meta">
            <div>
              <span>{values.issuedAt ? "Issued at" : "Created at"}</span>
              <span className="rcti-type-emphasis">
                {rctiTimestamp(values.issuedAt || values.createdDate)}
              </span>
            </div>
            <div>
              <span>Issue date</span>
              <span className="rcti-type-emphasis">
                {rctiTimestamp(values.rctiDate)}
              </span>
            </div>
            <div>
              <span>Supply date</span>
              <span className="rcti-type-emphasis">
                {values.supplyDate
                  ? dateValue(values.supplyDate)
                  : "Not recorded"}
              </span>
            </div>
            <div>
              <span>RCTI reference</span>
              <span className="rcti-type-emphasis">
                {values.purchaseOrderNumber || values.projectReference || "—"}
              </span>
            </div>
            {values.rctiAgreementID && (
              <div>
                <span>Accepted agreement</span>
                <span className="rcti-type-emphasis">
                  AGR-{String(values.rctiAgreementID).slice(-8).toUpperCase()}
                </span>
              </div>
            )}
            <div>
              <span>Currency</span>
              <span className="rcti-type-emphasis">{currencyCode}</span>
            </div>
          </div>
          <hr />
          <h2 className="rcti-detail-section-heading type-h4">
            Items &amp; pricing
          </h2>
          <p className="rcti-detail-section-description">
            {values.gstType === "No GST"
              ? "No GST applies"
              : values.gstType === "Exclusive"
              ? "GST is added to the unit prices"
              : "Unit prices and line totals include GST"}{" "}
            · {currencyCode}
          </p>
        </>
      )}
      {issuedDetail && !values.versions?.length && (
        <dl className="rcti-c13-original-summary">
          <div>
            <dt>Subtotal</dt>
            <dd>{money(subtotalExcludingGst)}</dd>
          </div>
          <div>
            <dt>GST</dt>
            <dd>{money(values.totalGSTAmount)}</dd>
          </div>
          <div>
            <dt>Original total {currencyCode}</dt>
            <dd>{money(total)}</dd>
          </div>
        </dl>
      )}
      <div
        className="rcti-document-table-scroll"
        role="region"
        aria-label="RCTI items and pricing"
        tabIndex={0}
      >
        <table className="rcti-document-table">
          <thead>
            <tr>
              <th scope="col">Description</th>
              <th scope="col">Qty</th>
              <th scope="col">Unit price</th>
              <th scope="col">
                {issuedDetail ? "GST" : "GST amount / Taxable"}
              </th>
              <th scope="col">Discount</th>
              <th scope="col">Line total</th>
            </tr>
          </thead>
          <tbody>
            {items.length ? (
              items.map((item, index) => {
                const tax = rctiLineTax(item, values.gstType);
                return (
                  <tr key={item._id || item.itemID || index}>
                    <td>
                      <strong>
                        {item.itemName || item.itemDescription || "Item"}
                      </strong>
                      {item.itemName && item.itemDescription && (
                        <small>{item.itemDescription}</small>
                      )}
                    </td>
                    <td>{item.itemQuantity || 0}</td>
                    <td>{money(item.itemPrice)}</td>
                    <td>
                      {money(tax.amount)}
                      <small>
                        {tax.rate}% · {tax.taxable ? "Taxable" : "Not taxable"}
                      </small>
                    </td>
                    <td>
                      {issuedDetail && !Number(item.discountAmount)
                        ? "None"
                        : money(item.discountAmount)}
                    </td>
                    <td>
                      <strong>{money(item.totalItemPrice)}</strong>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={6}>No items were returned for this RCTI.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="invoice-content-summary rcti-detail-summary">
        <div className="rcti-c13-notes">
          {issuedDetail && <span>Notes</span>}
          {issuedDetail && legalNotes && <p>{legalNotes}</p>}
          {!issuedDetail && (values.supplierNotes || values.scope)?.trim() && (
            <div>
              <span>Supplier / scope notes</span>
              <p>{values.supplierNotes || values.scope}</p>
            </div>
          )}
          {!issuedDetail && values.rctiNotes?.trim() && (
            <div>
              <span>RCTI notes</span>
              <p>{values.rctiNotes}</p>
            </div>
          )}
        </div>
        <div>
          <p>
            <span>
              {values.gstType === "No GST"
                ? "Total discounts"
                : "Total discounts (GST inclusive)"}
            </span>
            <span className="rcti-type-emphasis">
              {money(values.totalDiscountAmount)}
            </span>
          </p>
          <p>
            <span>Subtotal excluding GST</span>
            <span className="rcti-type-emphasis">
              {money(subtotalExcludingGst)}
            </span>
          </p>
          <p>
            <span>
              {values.gstType === "No GST" ? "No GST" : gstSummaryLabel}
            </span>
            <span className="rcti-type-emphasis">
              {money(values.totalGSTAmount)}
            </span>
          </p>
          <hr />
          <p className="grand">
            <span>
              {values.gstType === "No GST" ? "Total" : "Total including GST"} ·{" "}
              {currencyCode}
            </span>
            <span className="rcti-type-emphasis">{money(total)}</span>
          </p>
          <p className="rcti-summary-explainer">
            {values.gstType === "No GST"
              ? "No GST applies. Discounts are already applied."
              : "GST is included in the total. Discounts are already applied."}
          </p>
        </div>
      </div>
      {!issuedDetail && <hr />}
      <p className="rcti-detail-section-description">
        The supplier is liable to pay the GST on these supplies.
        <br />
        Issued under the RCTI agreement between the parties, wording v1.0.
      </p>
      {issuedDetail && issued && (
        <div className="rcti-c13-legal-lock">
          <LuLockKeyhole aria-hidden="true" />
          <p>
            {values.versions?.length
              ? `Issued version ${values.version || 2}`
              : "Legal original"}{" "}
            · Issued {rctiTimestamp(values.issuedAt || values.rctiDate)}. Issued
            values are locked; corrections are recorded as linked versions. The
            original is retained in history.
          </p>
        </div>
      )}
      {showAttachments && (
        <>
          <div className="rcti-detail-attachments">
            <span>Attachments</span>
            <span>
              {documents.length
                ? `${documents.length} attachment${
                    documents.length === 1 ? "" : "s"
                  }`
                : "No attachments added"}
            </span>
          </div>
          {documents.map((document, index) => (
            <button
              type="button"
              className="rcti-document-link"
              onClick={() => downloadRctiAttachment(document)}
              key={document._id || document.documentFileName || index}
            >
              <LuFileText />
              <span>
                {document.documentName ||
                  document.documentFileName ||
                  `Attachment ${index + 1}`}
              </span>
              <LuDownload />
            </button>
          ))}
        </>
      )}
      {values.paymentInstruction && (
        <div className="rcti-detail-instructions">
          <span>Payment instructions</span>
          <span>
            {values.paymentInstruction || "No payment instructions configured"}
          </span>
        </div>
      )}
    </section>
  );
}

RCTIDocument.propTypes = {
  issuedDetail: PropTypes.bool,
  agreement: PropTypes.object,
  values: PropTypes.object.isRequired,
  items: PropTypes.array.isRequired,
  documents: PropTypes.array.isRequired,
  organisation: PropTypes.string.isRequired,
  rctiID: PropTypes.string,
  loadingPage: PropTypes.bool,
  currencyCode: PropTypes.string.isRequired,
  subtotalExcludingGst: PropTypes.number.isRequired,
  gstSummaryLabel: PropTypes.string.isRequired,
  total: PropTypes.number.isRequired,
  money: PropTypes.func.isRequired,
  dateValue: PropTypes.func.isRequired,
  showAttachments: PropTypes.bool,
};
